// SolutionConfigForm — the ONE config form of the reconciliation loop (ADR-056
// §6), judged in Workbench/Solution reconciliation. Two modes answering "what
// happened?": 'changed' = a new period since a date ≤ today (an earlier date
// lands in history — first-class), 'correct' = repair a period's values in
// place (no date, no new period). The field body is the shared
// SolutionConfigFields (source blocks — also hosted by the create wizard's
// Solution step), so the surfaces cannot drift; this drawer owns only the
// changed-mode meta (date + reason), the schema, and the submit. The form holds
// the row it writes beneath (the site — ADR-084 §4): it seeds from it at each
// open, hands the save the row's stamp as the form opened, and AWAITS the host's
// save — a refusal stays in the drawer, on its line (a stale one with Load the
// latest), and the drawer closes only on success.

import { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { z } from 'zod';
import type { TFunction } from 'i18next';

import { DrawerForm } from './forms/DrawerForm.js';
import { useFormShell, type StampedRow } from './forms/useFormShell.js';
import { DatePicker } from '../ui/DatePicker.js';
import { Label } from '../ui/Label.js';
import { Textarea } from '../ui/Textarea.js';
import { formatDate } from '../../lib/format.js';
import { todayIn } from '../../lib/period.js';
import type { WeekStart } from '@ems-app/schemas/common/period';
import { OBSERVABLE_FIELD_KIND, type DiscoverableConfigField } from '@ems-app/schemas/sites/discoveries';
import type { SiteSuggestion } from '@ems-app/schemas/sites/responses';
import { SolutionConfigFields } from './SolutionConfigFields.js';
import { FORM_SECTIONS, stripOffSections, type ConfigValues } from './solutionConfig.js';


function shiftDay(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

type FormVals = Record<string, string | number | boolean | null>;

export interface SolutionConfigFormProps<TRow extends StampedRow = StampedRow> {
  mode: 'changed' | 'correct';
  /** The row the form writes beneath (the site) — read at each open; its stamp rides the save. Absent: no row. */
  row?: TRow | null;
  /** The values being edited (the target period's, or blanks for a first config) — or how the row gives them, read
   *  at each open and on Load the latest. */
  initial: ConfigValues | ((row: TRow | null) => ConfigValues);
  /** Load the latest: the fresh row, or null when it (or the corrected period) is gone. */
  reload?: () => Promise<TRow | null>;
  /** The corrected period's start (the title's "since {date}") — correct mode. */
  targetStart?: string | null;
  /** The earliest period's start — a change must land after known history begins. */
  earliestStart?: string | null;
  /** Every period boundary — a change on an existing boundary is a correction instead. */
  boundaries?: string[];
  /** OPEN suggestions — each drifted field gets its discovered hint + one-click adopt. */
  suggestions?: SiteSuggestion[];
  focusField?: DiscoverableConfigField;
  /** The site's zone — "today", the latest allowed start, is the site's day (ADR-082 §6), never the machine's. */
  timeZone?: string;
  /** The project's week start — the calendar grid's first column (ADR-082 §4). */
  weekStart?: WeekStart;
  /** Persist — awaited: throw to keep the drawer open with the refusal on its line; resolve and the drawer closes. */
  onSubmit: (values: ConfigValues, meta: { effective_from?: string; change_reason?: string | null; stamp: number | undefined }) => void | Promise<void>;
  onClose: () => void;
  t: TFunction;
}

export function SolutionConfigForm<TRow extends StampedRow = StampedRow>({
  mode,
  row,
  initial,
  reload,
  targetStart,
  earliestStart,
  boundaries = [],
  suggestions = [],
  focusField,
  timeZone,
  weekStart,
  onSubmit,
  onClose,
  t,
}: SolutionConfigFormProps<TRow>) {
  const today = todayIn(timeZone ?? 'UTC');
  const boundarySet = useMemo(() => new Set(boundaries), [boundaries]);
  const open = suggestions.filter((s) => s.status === 'open');

  const fieldKeys = useMemo(() => {
    const keys: DiscoverableConfigField[] = [];
    for (const { flag, fields } of FORM_SECTIONS) {
      if (flag) keys.push(flag);
      keys.push(...fields);
    }
    return keys;
  }, []);

  const schema = useMemo(() => {
    const shape: Record<string, z.ZodTypeAny> = {};
    for (const f of fieldKeys) {
      const kind = OBSERVABLE_FIELD_KIND[f];
      shape[f] = kind === 'boolean' ? z.boolean() : kind === 'text' || kind === 'battery_chemistry' ? z.string().nullable() : z.number().nullable();
    }
    if (mode === 'changed') {
      shape.effective_from = z
        .string()
        .nullable()
        .refine((v) => v !== null && v !== '', { message: t('reconcile.form.dateRequired') })
        .refine((v) => v === null || v <= today, { message: t('reconcile.form.dateFuture') })
        .refine((v) => v === null || earliestStart === null || earliestStart === undefined || v > earliestStart, {
          message: t('reconcile.form.dateBeforeHistory', { date: earliestStart ? formatDate(earliestStart, 'date') : '' }),
        })
        .refine((v) => v === null || !boundarySet.has(v), { message: t('reconcile.form.dateOnBoundary') });
      shape.change_reason = z.string();
    }
    return z.object(shape) as unknown as z.ZodType<FormVals, FormVals>;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, earliestStart, boundarySet, fieldKeys]);

  // The seed from the row as it is at the open (never reactive — a refetch while open moves nothing).
  const seedOf = (r: TRow | null): FormVals => {
    const init = typeof initial === 'function' ? initial(r) : initial;
    const d = Object.fromEntries(fieldKeys.map((f) => [f, init[f as keyof ConfigValues] ?? (OBSERVABLE_FIELD_KIND[f] === 'boolean' ? false : null)])) as FormVals;
    if (mode === 'changed') {
      d.effective_from = null;
      d.change_reason = '';
    }
    return d;
  };

  const shell = useFormShell<FormVals, TRow>({
    schema,
    row: row ?? null,
    values: seedOf,
    reload,
    onSubmit: async (values, { stamp }) => {
      const { effective_from, change_reason, ...fields } = values;
      // The values of a card switched OFF are not sent (stripped at both hosts' commits).
      await onSubmit(stripOffSections(fields as unknown as ConfigValues), {
        effective_from: mode === 'changed' ? String(effective_from) : undefined,
        change_reason: mode === 'changed' ? String(change_reason ?? '') || null : undefined,
        stamp,
      });
    },
    onSuccess: onClose,
  });

  return (
    <DrawerForm
      shell={shell}
      open
      onClose={onClose}
      title={
        mode === 'changed'
          ? t('reconcile.form.changedTitle')
          : targetStart
            ? t('reconcile.form.correctTitle', { date: formatDate(targetStart, 'date') })
            : t('reconcile.form.changedTitle')
      }
      description={mode === 'changed' ? t('reconcile.menu.changedHelp') : t('reconcile.menu.correctDataHelp')}
      submitLabel={mode === 'changed' ? t('reconcile.form.submitChanged') : t('reconcile.form.submitCorrect')}
      cancelLabel={undefined}
      maxWidth="max-w-xl"
      autoFocusField={focusField}
    >
      <div className="flex flex-col gap-4">
        {mode === 'changed' && (
          <>
            <div>
              <Controller
                control={shell.form.control}
                name="effective_from"
                render={({ field: f, fieldState }) => (
                  <DatePicker
                    label={t('reconcile.form.sinceWhen')}
                    value={typeof f.value === 'string' ? f.value : null}
                    onChange={f.onChange}
                    minValue={earliestStart ? shiftDay(earliestStart, 1) : undefined}
                    maxValue={today}
                    weekStart={weekStart}
                    zone={timeZone}
                    isDateUnavailable={(iso) => boundarySet.has(iso)}
                    error={fieldState.error?.message}
                    focusOnMount={!focusField}
                  />
                )}
              />
              <p className="mt-1 text-xs text-[var(--color-text-secondary)]">
                {t('reconcile.form.sinceHelp', {
                  earliest: earliestStart ? formatDate(earliestStart, 'date') : formatDate(today, 'date'),
                })}
              </p>
            </div>
            <div>
              <Label htmlFor="fld-change_reason">{t('reconcile.form.reason')}</Label>
              <Textarea id="fld-change_reason" rows={2} placeholder={t('reconcile.form.reasonPlaceholder')} {...shell.form.register('change_reason')} />
            </div>
          </>
        )}
        <SolutionConfigFields form={shell.form} suggestions={open} t={t} />
      </div>
    </DrawerForm>
  );
}
